/**
 * Whether the TrustArc consent banner must be hidden on the current route.
 *
 * Matches the current pathname against the per-locale `consentBannerDisabledRoutes` list
 * (from the Statsig locale config, e.g. `['/privacy-policy']` for UK). Tolerates an
 * optional locale prefix (e.g. `/uk/privacy-policy`), a trailing slash, and any
 * query/hash. Because the routes live in the per-locale config, a route only
 * suppresses the banner for the locale that lists it.
 *
 * Empty / absent list ⇒ banner shows everywhere (legacy behaviour).
 */
export const isBannerDisabledRoute = (pathname?: string | null, disabledRoutes?: string[]): boolean => {
  if (!pathname || !disabledRoutes?.length) return false

  // Drop query/hash and any trailing slash so `/privacy-policy?x=1/` still matches.
  // (`split` always yields at least one element; the `= ''` default only satisfies TS.)
  const [pathOnly = ''] = pathname.split(/[?#]/)
  const path = pathOnly.replace(/\/+$/, '') || '/'

  return disabledRoutes.some(route => {
    const normalisedRoute = (route ?? '').replace(/\/+$/, '')
    if (!normalisedRoute) return false
    // Exact match, or the route appears as a leading-locale-prefixed suffix
    // (`/uk/privacy-policy`), or as the root of a deeper path (`/privacy-policy/x`).
    // Each route starts with `/`, so `endsWith` keeps the segment boundary intact.
    return path === normalisedRoute || path.endsWith(normalisedRoute) || path.startsWith(`${normalisedRoute}/`)
  })
}
